import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export type Detent = 'peek' | 'mid' | 'full';

const ORDER: Detent[] = ['peek', 'mid', 'full'];
/** how far past the last drag position we project, in ms of travel */
const FLING_MS = 140;
/** resistance applied when dragging past the top or bottom stop */
const RUBBER = 0.3;
/** downward travel before a drag on the list is treated as a sheet drag */
const TAKEOVER_PX = 8;

interface Metrics {
  sheetH: number;
  peekH: number;
  midH: number;
}

interface Props {
  /** false on desktop — the sheet renders as a static sidebar instead */
  enabled: boolean;
  detent: Detent;
  onDetentChange: (d: Detent) => void;
  /** how much of the sheet is on screen, reported after every settle */
  onVisibleHeight?: (px: number) => void;
  /** controls that stay reachable at the `peek` detent */
  head: ReactNode;
  children: ReactNode;
}

export default function BottomSheet({
  enabled,
  detent,
  onDetentChange,
  onVisibleHeight,
  head,
  children,
}: Props) {
  const rootRef = useRef<HTMLElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const metricsRef = useRef<Metrics>({ sheetH: 0, peekH: 0, midH: 0 });
  const [metrics, setMetrics] = useState<Metrics>(metricsRef.current);
  const detentRef = useRef(detent);
  detentRef.current = detent;

  const [offset, setOffset] = useState<number | null>(null);
  const dragRef = useRef<{
    startY: number;
    startOffset: number;
    /** live offset — read on release, since state may not have committed yet */
    cur: number;
    lastY: number;
    lastT: number;
    vel: number;
    moved: boolean;
  } | null>(null);

  const heightOf = useCallback((d: Detent, m: Metrics): number => {
    if (d === 'full') return m.sheetH;
    if (d === 'mid') return Math.min(m.sheetH, Math.max(m.peekH, m.midH));
    return m.peekH;
  }, []);

  const offsetOf = useCallback(
    (d: Detent, m: Metrics = metricsRef.current) => Math.max(0, m.sheetH - heightOf(d, m)),
    [heightOf],
  );

  /* ---------- measurement ---------- */
  useLayoutEffect(() => {
    if (!enabled) return;
    const measure = () => {
      const root = rootRef.current;
      if (!root) return;
      const sheetH = root.getBoundingClientRect().height;
      const peekH = headRef.current?.getBoundingClientRect().height ?? 0;
      const next = {
        sheetH,
        peekH,
        midH: Math.round(window.innerHeight * 0.52),
      };
      const prev = metricsRef.current;
      if (prev.sheetH === next.sheetH && prev.peekH === next.peekH && prev.midH === next.midH) return;
      metricsRef.current = next;
      setMetrics(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (rootRef.current) ro.observe(rootRef.current);
    if (headRef.current) ro.observe(headRef.current);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [enabled]);

  /* ---------- report visible height (drives the map's bottom inset) ---------- */
  useEffect(() => {
    if (!onVisibleHeight) return;
    onVisibleHeight(enabled ? heightOf(detent, metrics) : 0);
  }, [enabled, detent, metrics, heightOf, onVisibleHeight]);

  /* ---------- drag ---------- */
  const beginDrag = useCallback(
    (y: number) => {
      const start = offsetOf(detentRef.current);
      dragRef.current = {
        startY: y,
        startOffset: start,
        cur: start,
        lastY: y,
        lastT: performance.now(),
        vel: 0,
        moved: false,
      };
      setOffset(start);
    },
    [offsetOf],
  );

  const moveDrag = useCallback((y: number) => {
    const d = dragRef.current;
    if (!d) return;
    const now = performance.now();
    const dt = now - d.lastT;
    if (dt > 0) d.vel = (y - d.lastY) / dt;
    d.lastY = y;
    d.lastT = now;
    if (Math.abs(y - d.startY) > 3) d.moved = true;

    const max = Math.max(0, metricsRef.current.sheetH - metricsRef.current.peekH);
    let next = d.startOffset + (y - d.startY);
    if (next < 0) next *= RUBBER;
    else if (next > max) next = max + (next - max) * RUBBER;
    d.cur = next;
    setOffset(next);
  }, []);

  const endDrag = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    setOffset(null);
    if (!d) return false;
    const m = metricsRef.current;
    // fling: project where the sheet is heading, then snap to the nearest stop
    const projected = m.sheetH - d.cur - d.vel * FLING_MS;
    let bestDetent: Detent = detentRef.current;
    let bestGap = Infinity;
    for (const cand of ORDER) {
      const gap = Math.abs(heightOf(cand, m) - projected);
      if (gap < bestGap) {
        bestGap = gap;
        bestDetent = cand;
      }
    }
    if (d.moved) onDetentChange(bestDetent);
    return d.moved;
  }, [heightOf, onDetentChange]);

  /* pointer drag from the grab strip */
  const suppressClick = useRef(false);
  const onHandlePointerDown = (e: React.PointerEvent) => {
    if (!enabled || e.button !== 0) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    beginDrag(e.clientY);
  };
  const onHandlePointerMove = (e: React.PointerEvent) => {
    if (dragRef.current) moveDrag(e.clientY);
  };
  const onHandlePointerUp = () => {
    if (dragRef.current) suppressClick.current = endDrag();
  };
  /** a drag ends with a click on the handle — only a genuine tap should cycle */
  const onHandleClick = () => {
    if (suppressClick.current) {
      suppressClick.current = false;
      return;
    }
    const i = ORDER.indexOf(detent);
    onDetentChange(ORDER[(i + 1) % ORDER.length]);
  };

  /* drag the sheet by pulling the list down once it is scrolled to the top */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) return;
    let startY = 0;
    let armed = false;
    let dragging = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      startY = e.touches[0].clientY;
      armed = el.scrollTop <= 0;
      dragging = false;
    };
    const onMove = (e: TouchEvent) => {
      if (!armed || e.touches.length !== 1) return;
      const y = e.touches[0].clientY;
      const dy = y - startY;
      if (!dragging) {
        if (dy < -2) {
          armed = false; // scrolling up into the list — hand it back to the browser
          return;
        }
        if (dy < TAKEOVER_PX || el.scrollTop > 0) return;
        dragging = true;
        beginDrag(y);
      }
      e.preventDefault();
      moveDrag(y);
    };
    const onEnd = () => {
      if (dragging) endDrag();
      dragging = false;
      armed = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [enabled, beginDrag, moveDrag, endDrag]);

  /* collapsing the sheet should also rewind the list */
  useEffect(() => {
    if (enabled && detent === 'peek') scrollRef.current?.scrollTo({ top: 0 });
  }, [enabled, detent]);

  const dragging = offset != null;
  const y = enabled ? (offset ?? offsetOf(detent, metrics)) : 0;

  return (
    <section
      ref={rootRef}
      id="sheet"
      className={`detent-${detent}${dragging ? ' dragging' : ''}`}
      style={enabled ? { transform: `translate3d(0, ${y}px, 0)` } : undefined}
      aria-label="Forecast controls and beach ranking"
    >
      <div ref={headRef} className="sheet-head">
        <button
          type="button"
          className="sheet-grab"
          onPointerDown={onHandlePointerDown}
          onPointerMove={onHandlePointerMove}
          onPointerUp={onHandlePointerUp}
          onPointerCancel={onHandlePointerUp}
          onClick={onHandleClick}
          aria-label={detent === 'full' ? 'Collapse panel' : 'Expand panel'}
          aria-expanded={detent !== 'peek'}
        >
          <span className="grabline" />
        </button>
        {head}
      </div>
      {/*
        At a partial detent the sheet's lower edge hangs below the viewport, so
        the tail of the list would be unreachable. Pad the scroller by exactly
        the hidden amount — settled offset only, so dragging doesn't re-layout.
      */}
      <div
        ref={scrollRef}
        className="sheet-scroll"
        style={enabled ? ({ '--sheet-hidden': `${offsetOf(detent, metrics)}px` } as CSSProperties) : undefined}
      >
        {children}
      </div>
    </section>
  );
}
